/**
 * Client-side marketing parameter utilities
 */
import { getCookie } from '../utils'
import { FBCLID_PROVENANCE_PARAM, markCarriedFbclid } from './click-provenance'
import { DEFAULT_SOURCE, MARKETING_PARAM_KEYS } from './config'
import type { MarketingParameters, MarketingParamsResult } from './types'

/**
 * Extract marketing parameters from URLSearchParams
 */
export function extractMarketingParams(searchParams: URLSearchParams): MarketingParameters {
  const params: MarketingParameters = {}

  MARKETING_PARAM_KEYS.forEach((key) => {
    const value = searchParams.get(key)
    if (value) {
      params[key as keyof MarketingParameters] = value
    }
  })

  return params
}

/**
 * Get marketing parameters from current URL
 */
export function getCurrentMarketingParams(): MarketingParameters {
  if (typeof window === 'undefined') {
    return {}
  }

  const currentUrl = new URL(window.location.href)
  return extractMarketingParams(currentUrl.searchParams)
}

/**
 * Get marketing parameters from cookies
 */
export function getMarketingParamsFromCookies(): MarketingParameters {
  if (typeof document === 'undefined') {
    return {}
  }

  const params: MarketingParameters = {}

  MARKETING_PARAM_KEYS.forEach((key) => {
    const value = getCookie(key)
    if (value) {
      params[key as keyof MarketingParameters] = value
    }
  })

  return params
}

/**
 * Get marketing parameters with fallback to cookies
 * Priority: URL parameters > Cookies
 */
export function getMarketingParams(): MarketingParameters {
  const urlParams = getCurrentMarketingParams()

  // If we have any URL params, use only those
  if (Object.keys(urlParams).length > 0) {
    return urlParams
  }

  // Otherwise, fall back to cookies
  return getMarketingParamsFromCookies()
}

/**
 * Get marketing parameters with normalized source
 */
export function getMarketingParamsWithSource(): MarketingParamsResult {
  const params = getMarketingParams()

  return {
    ...params,
    source: params.source || DEFAULT_SOURCE,
  }
}

/**
 * Check if current URL has marketing parameters
 */
export function hasMarketingParams(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const currentUrl = new URL(window.location.href)
  return MARKETING_PARAM_KEYS.some((key) => currentUrl.searchParams.has(key))
}

/**
 * Check if a path should preserve marketing parameters
 * Only payment and subscription-related paths should preserve them
 */
function shouldPreserveMarketingParams(path: string): boolean {
  const preservePaths = [
    '/subscribes',
    '/payment',
    '/my-podo', // My page (subscription management)
  ]

  return preservePaths.some((preservePath) => path.includes(preservePath))
}

/**
 * Build a URL with preserved marketing parameters
 * Only adds marketing params for payment/subscription related pages
 */
export function buildUrlWithMarketingParams(targetPath: string, additionalParams?: Record<string, string>): string {
  const url = new URL(targetPath, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')

  // Only add marketing params for payment/subscription pages
  if (shouldPreserveMarketingParams(targetPath)) {
    // Get current marketing parameters (URL > Cookies)
    const marketingParams = getMarketingParams()

    // Add marketing parameters to URL
    Object.entries(marketingParams).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value)
      }
    })
  }

  // Add additional parameters
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })
  }

  // A copied URL/cookie value is not a new direct click, even in a fresh tab without session storage.
  markCarriedFbclid(url)
  return url.pathname + url.search
}

/**
 * Clean marketing parameters from a URL
 */
export function cleanMarketingParams(url: string): string {
  const urlObj = new URL(url, 'http://localhost')

  MARKETING_PARAM_KEYS.forEach((key) => {
    urlObj.searchParams.delete(key)
  })
  urlObj.searchParams.delete(FBCLID_PROVENANCE_PARAM)

  const search = urlObj.search
  return url.includes('://') ? urlObj.href : urlObj.pathname + search
}

/**
 * Parse marketing parameters from a URL string
 */
export function parseMarketingParams(url: string): MarketingParameters {
  try {
    const urlObj = new URL(url, 'http://localhost')
    return extractMarketingParams(urlObj.searchParams)
  } catch {
    return {}
  }
}

/**
 * Storage key for persisting marketing attribution
 */
const STORAGE_KEY = 'marketing_attribution'

/**
 * Save marketing parameters to localStorage for attribution
 */
export function saveMarketingAttribution(params?: MarketingParameters): void {
  if (typeof window === 'undefined') {
    return
  }

  const paramsToSave = params || getMarketingParams()

  if (Object.keys(paramsToSave).length > 0) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        params: paramsToSave,
        timestamp: Date.now(),
      }),
    )
  }
}

/**
 * Get saved marketing attribution from localStorage
 */
export function getSavedMarketingAttribution(): MarketingParameters | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (!saved) {
      return null
    }

    const { params, timestamp } = JSON.parse(saved)

    // Attribution expires after 30 days
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    if (Date.now() - timestamp > thirtyDaysInMs) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return params
  } catch {
    return null
  }
}
